import axios from '@/axiosConnection'
import i18n from '@/traductions/config'
import toastr from '@/toastr'

export default {
  namespaced: true,
  state: {
    token: (localStorage.getItem('token') !== null ? localStorage.getItem('token') : null),
    authenticated: false,
    id: (localStorage.getItem('id') !== null ? localStorage.getItem('id') : null),
    firstname: (localStorage.getItem('firstname') !== null ? localStorage.getItem('firstname') : null),
    lastname: (localStorage.getItem('lastname') !== null ? localStorage.getItem('lastname') : null),
    email: (localStorage.getItem('email') !== null ? localStorage.getItem('email') : null),
    roles: (localStorage.getItem('roles') !== null ? JSON.parse(localStorage.getItem('roles')) : null),
    authorizations: (localStorage.getItem('authorizations') !== null ? JSON.parse(localStorage.getItem('authorizations')) : null),
    // INC-001 : entrepôt de rattachement de l'utilisateur (null = accès à tous les entrepôts)
    warehouse: (localStorage.getItem('warehouse') !== null && localStorage.getItem('warehouse') !== 'null' ? JSON.parse(localStorage.getItem('warehouse')) : null),
  },
  mutations: {
    auth_success(state, token) {
      state.authenticated = true
      state.token = token
      // axios.defaults.headers.common['Authorization'] = 'Bearer ' + token
    },
    logout(state) {
      state.status = ''
      state.token = ''
      state.authenticated = false
      // delete axios.defaults.headers['Authorization']
    },
    authenticated(state, authenticated) {
      state.authenticated = authenticated
    },
    id(state, id) {
      state.id = id
    },
    firstname(state, firstname) {
      state.firstname = firstname
    },
    lastname(state, lastname) {
      state.lastname = lastname
    },
    email(state, email) {
      state.email = email
    },
    roles(state, roles) {
      state.roles = roles
    },
    warehouse(state, warehouse) {
      state.warehouse = warehouse
    },
    authorize(state, authorizations) {
      state.authorizations = authorizations
    },
  },
  getters: {
    token: state => state.token,
    authStatus: state => state.status,
    id: state => state.id,
    email: state => state.email,
    roles: state => state.roles,
    // INC-001 : entrepôt de l'utilisateur (objet {id, name, internal_code}) ou null = accès à tout
    warehouse: state => state.warehouse,
    fullname: state => state.firstname + ' ' + state.lastname,
    isAuthenticated: state => {
      if (state.token) {
        axios.defaults.headers.common['Authorization'] = 'Bearer ' + state.token
        return true
      } else {
        return state.authenticated
      }
    },

    /**
     * Authorize if authorization's value is true (fast way) or if authorization's value is stored as true in authorizations_list
     */
    isAuthorized: (state, getters) =>
      authorization =>
      ((
          state.authorizations !== null &&
          (
            state.authorizations[authorization] === null ||
            state.authorizations[authorization] === true
          )
        ) ||
        authorization === true) ||
      (getters.roles && getters.roles.includes('ROLE_SUPER_ADMIN')),
    isMobileUser: (state, getters) => getters.roles && !getters.roles.includes('ROLE_SUPER_ADMIN') && getters.isAuthorized('mobile_force-access')
  },
  actions: {
    login({
      commit
    }, args) {
      return new Promise((resolve, reject) => {
        const defaultValue = axios.defaults.baseURL
        axios.defaults.baseURL = axios.defaults.baseURL.split('/api')[0]
        delete axios.defaults.headers.common['Authorization']

        axios.post('/token', args).then(response => {
          const { access_token, refresh_token } = response.data

          localStorage.setItem('refresh_token', refresh_token)
          localStorage.setItem('token', access_token)
          commit('auth_success', access_token)
          resolve(response)
        }).catch(error => {
          localStorage.removeItem('token')
          toastr.error(i18n.t(error.response.data.status))

          reject(error)
        })
        axios.defaults.baseURL = defaultValue
      })
    },
    /**
     * PREPROD uniquement : ouvre une session à partir d'une adresse e-mail ou d'un
     * id utilisateur. Le back ne répond sur /auth_preprod/{identifier} qu'en
     * preprod (PREPROD_MOD='true') ou en dev ; il renvoie alors un token
     * « preprod-<id> » accepté par AzureAuthenticator.
     */
    loginPreprod({
      commit,
      dispatch
    }, identifier) {
      return new Promise((resolve, reject) => {
        // Route servie à la racine du back (hors /api), donc URL absolue :
        // le baseURL d'axios pointe sur /api/v1/.
        const backUrl = axios.defaults.baseURL.split('/api')[0]

        axios.get(`${backUrl}/auth_preprod/${encodeURIComponent(identifier)}`, {
          errorHandle: false,
          successHandle: false
        }).then(response => {
          const { access_token, refresh_token } = response.data

          // Purge la session précédente (id, autorisations, entrepôt...) pour ne
          // pas mélanger les données de l'ancien utilisateur avec le nouveau.
          dispatch('logout').then(() => {
            // logout() ne purge pas l'entrepôt : on le fait ici pour ne pas
            // afficher celui de l'utilisateur précédent avant la réponse de /profile.
            localStorage.removeItem('warehouse')
            localStorage.setItem('refresh_token', refresh_token)
            localStorage.setItem('token', access_token)
            commit('auth_success', access_token)
            resolve(response)
          })
        }).catch(error => {
          reject(error)
        })
      })
    },
    logout({
      commit
    }) {
      return new Promise((resolve, reject) => {
        commit('logout')
        localStorage.removeItem('token')
        localStorage.removeItem('id')
        localStorage.removeItem('firstname')
        localStorage.removeItem('lastname')
        localStorage.removeItem('email')
        localStorage.removeItem('roles')
        localStorage.removeItem('authorizations')
        resolve()
      })
    },
    setAuthenticated({
      commit
    }, authenticated) {
      commit('authenticated', authenticated)
    },
    refreshAuthorizations({
      commit,
      getters
    }) {
      return new Promise((resolve, reject) => {
        // Comme les autorisations sont nécessaires au fonctionnement de toute l'appli, on les charge avant de lancer tout le reste
        // donc pour ne pas tout bloquer si on les a déjà, on vérifie, et on lance le résolve si on les a déjà.
        // En parallèle, on les met quand même à jour à chaque fois par sécurité.
        if (getters.id) {
          resolve()
        }

        // Call the profile page to retrieve current user's informations.
        axios.get('profile', {
          successHandle: false,
        }).then(response => {

          commit('id', response.data.id)
          commit('firstname', response.data.firstname)
          commit('lastname', response.data.lastname)
          commit('email', response.data.email)
          commit('roles', response.data.roles)
          commit('warehouse', response.data.warehouse || null)

          // On sauvegarde les infos de l'utilisateur en localStorage
          localStorage.setItem('id', response.data.id)
          localStorage.setItem('firstname', response.data.firstname)
          localStorage.setItem('lastname', response.data.lastname)
          localStorage.setItem('email', response.data.email)
          localStorage.setItem('roles', JSON.stringify(response.data.roles))
          localStorage.setItem('warehouse', JSON.stringify(response.data.warehouse || null))

          // Store current user's permissions
          let authorizations = response.data.authorization_list
          commit('authorize', authorizations)

          // On sauvegarde les autorisations en localStorage car sinon, à chaque rechargement de page,
          // l'utilisateur doit attendre que ses autorisations soient reçu de l'API et se retrouve
          // donc avec une interface vide
          localStorage.setItem('authorizations', JSON.stringify(authorizations))
          resolve()
        }).catch(error => {
          reject()
        })
      })
    },
  }
}



// WEBPACK FOOTER //
// ./src/stores/modules/auth.js